/**
 * DepartmentTaskBreakdown
 * Displays task distribution by priority and status
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
} from '@mui/material';
import {
  PieChart,
  AlertCircle,
  Flag,
  Activity,
} from 'lucide-react';

import { DepartmentTaskDistribution } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';

interface DepartmentTaskBreakdownProps {
  distribution: DepartmentTaskDistribution | undefined;
  isLoading: boolean;
}

type ViewType = 'priority' | 'status';

const DepartmentTaskBreakdown: React.FC<DepartmentTaskBreakdownProps> = ({
  distribution,
  isLoading,
}) => {
  const theme = useTheme();
  const [view, setView] = useState<ViewType>('priority');

  if (isLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width={150} />
          <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (!distribution) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography color="text.secondary">No distribution data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const priorityData = [
    { label: 'Critical (P1)', value: distribution.tasksByPriority.p1, color: '#ef4444' },
    { label: 'High (P2)', value: distribution.tasksByPriority.p2, color: '#f97316' },
    { label: 'Medium (P3)', value: distribution.tasksByPriority.p3, color: '#4F46E5' },
    { label: 'Low (P4)', value: distribution.tasksByPriority.p4, color: '#6b7280' },
  ];

  const statusData = [
    { label: 'Not Started', value: distribution.tasksByStatus.notStarted, color: '#6b7280' },
    { label: 'In Progress', value: distribution.tasksByStatus.inProgress, color: '#4F46E5' },
    { label: 'Blocked', value: distribution.tasksByStatus.blocked, color: '#f97316' },
    { label: 'Completed', value: distribution.tasksByStatus.completed, color: '#22c55e' },
    { label: 'Cancelled', value: distribution.tasksByStatus.cancelled, color: '#94a3b8' },
  ];

  const currentData = view === 'priority' ? priorityData : statusData;
  const total = currentData.reduce((sum, item) => sum + item.value, 0);

  // Calculate angles for donut chart
  let currentAngle = 0;
  const segments = currentData.map(item => {
    const percentage = total > 0 ? (item.value / total) * 100 : 0;
    const angle = (percentage / 100) * 360;
    const segment = {
      ...item,
      percentage,
      startAngle: currentAngle,
      endAngle: currentAngle + angle,
    };
    currentAngle += angle;
    return segment;
  }).filter(s => s.value > 0);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PieChart size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Task Distribution
            </Typography>
          </Box>
        </Box>

        {/* View Toggle */}
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, value) => value && setView(value)}
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="priority" sx={{ textTransform: 'none', px: 2 }}>
            <Flag size={16} style={{ marginRight: 6 }} />
            By Priority
          </ToggleButton>
          <ToggleButton value="status" sx={{ textTransform: 'none', px: 2 }}>
            <Activity size={16} style={{ marginRight: 6 }} />
            By Status
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Donut Chart Visualization */}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <Box sx={{ position: 'relative', width: 180, height: 180 }}>
            <svg width="180" height="180" viewBox="0 0 180 180">
              {segments.map((segment, index) => {
                const radius = 70;
                const innerRadius = 50;
                const cx = 90;
                const cy = 90;
                
                const startAngleRad = (segment.startAngle - 90) * (Math.PI / 180);
                const endAngleRad = (segment.endAngle - 90) * (Math.PI / 180);
                
                const x1 = cx + radius * Math.cos(startAngleRad);
                const y1 = cy + radius * Math.sin(startAngleRad);
                const x2 = cx + radius * Math.cos(endAngleRad);
                const y2 = cy + radius * Math.sin(endAngleRad);
                
                const x3 = cx + innerRadius * Math.cos(endAngleRad);
                const y3 = cy + innerRadius * Math.sin(endAngleRad);
                const x4 = cx + innerRadius * Math.cos(startAngleRad);
                const y4 = cy + innerRadius * Math.sin(startAngleRad);
                
                const largeArcFlag = segment.endAngle - segment.startAngle > 180 ? 1 : 0;
                
                const d = [
                  `M ${x1} ${y1}`,
                  `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                  `L ${x3} ${y3}`,
                  `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
                  'Z'
                ].join(' ');
                
                return (
                  <path
                    key={index}
                    d={d}
                    fill={segment.color}
                    stroke="white"
                    strokeWidth="2"
                    style={{ transition: 'all 0.3s' }}
                  />
                );
              })}
            </svg>
            {/* Center text */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}
            >
              <Typography variant="h4" fontWeight={700}>
                {total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tasks
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {currentData.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                borderRadius: 1,
                bgcolor: alpha(item.color, 0.08),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: item.color,
                  }}
                />
                <Typography variant="body2" fontWeight={500}>
                  {item.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({formatPercent(total > 0 ? (item.value / total) * 100 : 0)})
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Team Distribution */}
        {distribution.tasksByTeam && distribution.tasksByTeam.length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Tasks by Team
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {distribution.tasksByTeam.map((team) => (
                <Box
                  key={team.teamId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2">{team.teamName}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {team.completedCount}/{team.taskCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      completed
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DepartmentTaskBreakdown;
